import { FC, useState } from 'react';

import pluralize from 'pluralize';

import { EntertainerBadge } from '@queries/CritiqueQueries';
import {
  useCurrentEntertainerBadgesQuery,
  useCurrentEntertainerQuery,
} from '@queries/EntertainerQueries';

import { useAnalytics } from '@hooks/useAnalytics';

import { useRouter } from '@router/index';

import Button from '@components/buttons/Button';
import { SendEntertainerBadgeButton } from '@components/buttons/SendEntertainerBadgeButton';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { List } from '@components/layout/List/List';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { UnlockBadgePopup } from './UnlockBadgePopup';

export const ReceivedBadgesContent: FC<{ canShareBadges?: boolean }> = ({ canShareBadges }) => {
  const { track } = useAnalytics();
  const { navigate } = useRouter();
  const { entertainer } = useCurrentEntertainerQuery();
  const { entertainerBadgesAreReady, receivedBadges = [] } = useCurrentEntertainerBadgesQuery(
    entertainer?._id,
  );
  const [lockedBadgeId, setLockedBadgeId] = useState<string>();

  const handleShareBadge = () => {
    navigate('/engagement/support', {
      state: {
        forceBack: true,
      },
    });
  };

  const handleUnlockBadge = (badge: EntertainerBadge) => {
    if (badge.isLocked) {
      track('Click unlock badge');
      setLockedBadgeId(badge._id);
    }
  };

  return (
    <>
      <TitleToolbar
        className="mb-2"
        text={`${receivedBadges?.length} ${pluralize('Achievement', receivedBadges?.length)} Earned`}
        size="sm"
        action={
          canShareBadges && (
            <Button
              size="sm"
              className="px-3 py-2"
              roundness="rounded"
              variant="flat"
              color="primary"
              onClick={handleShareBadge}
            >
              Share Badges
            </Button>
          )
        }
      />
      {entertainerBadgesAreReady ? (
        <>
          {receivedBadges?.length ? (
            <List
              className="notification-list mt-0"
              listItems={receivedBadges.map((badge) => ({
                startIcon: (
                  <img
                    src={badge.imageUrl}
                    style={{ height: 42, width: 42, marginTop: -2, marginBottom: -8 }}
                  />
                ),
                title: badge.isLocked
                  ? 'Locked'
                  : ((<span className="text-truncate">{badge?.from?.displayName}</span>) as any),
                subTitle: badge.isLocked
                  ? undefined
                  : ((
                      <>
                        <div>{`${badge.title} for ${badge.reason}`}</div>
                        {badge.eventTitle && (
                          <div>
                            <strong>{badge.eventTitle}</strong>
                          </div>
                        )}
                      </>
                    ) as any),
                endIcon: (
                  <div>
                    {badge.isLocked ? (
                      <Button
                        size="sm"
                        className="px-3 py-2"
                        roundness="rounded"
                        variant="smooth"
                        color="neutral"
                        onClick={() => handleUnlockBadge(badge)}
                      >
                        Unlock
                      </Button>
                    ) : (
                      <>
                        {badge?.from?.entertainerId && canShareBadges && (
                          <SendEntertainerBadgeButton
                            entertainerId={badge?.from?.entertainerId}
                            text="Share back"
                            buttonProps={{
                              size: 'sm',
                              startIcon: undefined,
                              className: 'px-3 py-2',
                              roundness: 'rounded',
                              variant: 'smooth',
                              color: 'neutral',
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                ),
              }))}
            />
          ) : (
            <EmptyState
              title="No badges yet"
              text="You haven't received any badges yet, but you can still send some to your fellow entertainers."
              button={
                canShareBadges && {
                  text: 'Share a badge',
                  onClick: handleShareBadge,
                }
              }
            />
          )}
        </>
      ) : (
        <>
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
          <Skeleton height={40} />
        </>
      )}
      <UnlockBadgePopup
        isVisible={!!lockedBadgeId}
        lockedBadgeId={lockedBadgeId}
        onClose={() => setLockedBadgeId(undefined)}
      />
    </>
  );
};
